import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { SharedActions } from "../actions";
import { map, switchMap, of, catchError, take } from "rxjs";
import { ApiService } from "../../../services/api.service";
import { GlobalService } from "../../../services/global.service";
import { StorageService } from "../../../services/storage.service";
import { SharedEffectsService } from "./shared-effect.service";
import { LoggerService, tryNormalizeHttpError } from "@spartacus/core";
import { CartsByLegalEntity } from "../../../models/cartsByLegalEntity.model";
import { LegalEntityList } from "../../../models/legalEntityList.model";
import { AppConstants, RouteCheck } from "../../../constants/app-constant";
import { Store } from "@ngrx/store";

@Injectable()
export class SharedEffects {

    protected logger = inject(LoggerService);
    private DEFAULT_CARTS_BY_LEGAL_ENTITY_PAYLOAD: {
        legalEntities: string | null;
        fields: string;
        currentPage: number;
        pageSize: number;
    };

    constructor(
        private actions$: Actions,
        private globalService: GlobalService,
        private storageService: StorageService,
        private apiService: ApiService,
        private sharedEffectsService: SharedEffectsService,
        private store: Store,
    ) {
        this.DEFAULT_CARTS_BY_LEGAL_ENTITY_PAYLOAD = {
            legalEntities: this.storageService.defaultLegalEntity?.uid ?? null,
            fields: 'FULL',
            currentPage: 0,
            pageSize: 5
        };
    }

    fetchLegalEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SharedActions.LoadLegalEntityList),
            switchMap((action) => {
                return this.apiService.getLegalEntityList(action.payload.currentPage, action.payload.fields, action.payload.pageSize, action.payload.userID)
                    .pipe(
                        map((data: LegalEntityList) => {
                            console.log('SharedLegalEntitiesEffects triggered..!');
                            this.globalService.loadingSubject.next(false);
                            return SharedActions.LoadLegalEntityListSuccess({ userLegalEntities: data.legalEntities })
                        }),
                        catchError((error) => {
                            this.globalService.loadingSubject.next(false); // Stop loading here
                            return of(
                                SharedActions.LoadLegalEntityListFail({
                                    payload: tryNormalizeHttpError(error, this.logger),
                                })
                            );
                        })
                    );
            }),
        )
    );

    legalEntityUpdated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SharedActions.UpdateLegalEntity),
            switchMap(() => {
                const paramsObject = {
                    legalEntities: this.storageService?.defaultLegalEntity?.uid || null,
                    fields: 'FULL',
                    currentPage: 0,
                    pageSize: 5
                };
                // Trigger Mini Cart api based on new legalEntity
                this.store.dispatch(SharedActions.loadCartsByLegalEntity(paramsObject));

                // Now, listen for the success of the loadCartsByLegalEntity action
                return this.actions$.pipe(
                    ofType(SharedActions.loadCartsByLegalEntitySuccess),
                    take(1),
                    map(() => {
                        this.globalService.checkAndTriggerPageActions([
                            RouteCheck.IS_HOME_PAGE,
                            RouteCheck.IS_PLP,
                            RouteCheck.IS_PDP,
                            RouteCheck.IS_MANAGE_CART,
                            RouteCheck.IS_CART
                        ]);
                        return { type: 'NO_ACTION' }; // returning a dummy action
                    })
                );
            }),
        )
    );

    createNewCart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SharedActions.createNewCart),
            switchMap(({ legalEntityId, cartName }) => {
                const paramsObject = {
                    ...AppConstants.DEFAULT_PAYLOAD,
                    legalEntityId,
                    cartName
                };
                return this.apiService.createCart(paramsObject)
            }),
            map(() => SharedActions.createNewCartSuccess({ payload: true })),
            catchError((error) => of(SharedActions.createNewCartFail({
                error: tryNormalizeHttpError(error, this.logger)
            })))
        )
    );

    cartsBylegalEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SharedActions.loadCartsByLegalEntity),
            map((props) => props ?? this.DEFAULT_CARTS_BY_LEGAL_ENTITY_PAYLOAD),
            switchMap((props) => {
                return this.apiService.getCartsListByLegalEntity(props)
                    .pipe(
                        map((data: CartsByLegalEntity) =>
                            this.sharedEffectsService.makeActiveCartAsFirst(data)
                        ),
                        switchMap((payload: CartsByLegalEntity) => of(SharedActions.loadCartsByLegalEntitySuccess({ payload }))),
                        catchError((error) => {
                            this.globalService.loadingSubject.next(false); // Stop loading here as well
                            return of(
                                SharedActions.loadCartsByLegalEntityFail({
                                    error: tryNormalizeHttpError(error, null),
                                })
                            );
                        })
                    )
            })
        )
    );

    UpdateLanguageAndDate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SharedActions.languageOrDateFormatUpdate),
            //update the method name to updateLanguageDate
            switchMap((action) => this.apiService.storeLanguageDate(action.payload)),
            map(() => SharedActions.languageOrDateFormatSuccess()),
            catchError((error) =>
                of(SharedActions.languageOrDateFormatFail(error))
            )
        )
    );
}